import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { eventApi } from '../../../core/api/rtk-query/event.api';
import { groupApi } from '../../../core/api/rtk-query/group.api';
import { userApi } from '../../../core/api/rtk-query/user.api';
import { getDayjsInGMT } from '../../../utils/dayjs.utils';

const Backdrop = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: grid; place-items: center; z-index: 500;
  overflow-y: auto; padding: 20px;
`;
const Modal = styled.div`
  width: 960px; max-width: 100%; background: #fff; border-radius: 16px; padding: 24px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.12); margin: auto;
`;
const Section = styled.div`
  margin-top: 20px; padding-top: 20px; border-top: 1px solid #e5e8f0;
`;
const SectionTitle = styled.h4`
  margin: 0 0 12px 0; font-size: 16px; font-weight: 600; color: #0b1221;
`;
const Row = styled.div`display: flex; gap: 8px; align-items: center;`;
const Input = styled.input`
  padding: 10px 12px; border-radius: 10px; border: 1px solid #dbe3f6;
  background: #fff; color: #0b1221; width: 100%; box-sizing: border-box;
`;
const Textarea = styled.textarea`
  padding: 10px 12px; border-radius: 10px; border: 1px solid #dbe3f6;
  background: #fff; color: #0b1221; width: 100%; box-sizing: border-box;
  min-height: 80px; resize: vertical; font-family: inherit;
`;
const Select = styled.select`
  padding: 10px 12px; border-radius: 10px; border: 1px solid #dbe3f6;
  background: #fff; color: #0b1221; width: 100%; box-sizing: border-box;
`;
const Btn = styled.button`
  background: #fff; border: 1px solid #dbe3f6; color: #0b1221;
  padding: 8px 12px; border-radius: 10px; cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const BtnPrimary = styled(Btn)`
  background: #0b68fe; color: #fff; border-color: #0b68fe;
`;
const Label = styled.label`
  display: block; font-size: 13px; font-weight: 500; color: #465676; margin-bottom: 6px;
`;
const Grid2 = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
`;
const ComboWrap = styled.div`position: relative;`;
const ComboList = styled.ul`
  position: absolute; left: 0; right: 0; top: calc(100% + 6px);
  z-index: 50; list-style: none; margin: 0; padding: 8px 0; max-height: 240px; overflow: auto;
  background: #fff; border: 1px solid #dbe3f6; border-radius: 12px; box-shadow: 0 8px 28px rgba(0,0,0,.08);
`;
const ComboItem = styled.li`
  padding: 8px 12px; cursor: pointer; white-space: nowrap; text-overflow: ellipsis; overflow: hidden;
  &:hover { background: #f7f9ff; }
`;
const UserList = styled.div`
  max-height: 300px; overflow-y: auto; border: 1px solid #e5e8f0; border-radius: 12px;
`;
const UserRow = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px 12px; border-bottom: 1px solid #eef1f6;
  &:last-child { border-bottom: none; }
`;

type Props = {
  eventId: string;
  onClose: () => void;
};

export const EventDetailsModal = ({ eventId, onClose }: Props) => {
  const { data: event, refetch: refetchEvent } = eventApi.useGetEventQuery(eventId);
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 25;
  const { data: usersPage, refetch: refetchUsers } = eventApi.useGetEventUsersQuery({ 
    id: eventId, 
    pageNumber, 
    pageSize 
  });

  // Редактирование
  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'Homework'|'Contest'|'Milestone'|'Exam'>('Homework');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [minimumPointsToPass, setMinimumPointsToPass] = useState(0);
  const [updateEvent] = eventApi.useUpdateEventMutation();

  // Добавление пользователей
  const [userQuery, setUserQuery] = useState('');
  const [userOpen, setUserOpen] = useState(false);
  const [triggerUserSearch] = userApi.useLazySearchUsersQuery();
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [addToUsers] = eventApi.useAddEventToUsersMutation();

  // Добавление группы
  const [groupId, setGroupId] = useState('');
  const { data: groups } = groupApi.useGetActiveAdminGroupsQuery({ pageNumber: 1, pageSize: 100 });
  const [grantForGroup] = eventApi.useGrantEventAccessForGroupMutation();

  // Удаление пользователя
  const [removeUserFromEvent] = eventApi.useRemoveUserFromEventMutation();

  useEffect(() => {
    if (event) {
      setName(event.name);
      setDescription(event.description || '');
      setType(event.type as any);
      setStartTime(getDayjsInGMT(event.startTime).format('YYYY-MM-DDTHH:mm'));
      setEndTime(getDayjsInGMT(event.endTime).format('YYYY-MM-DDTHH:mm'));
      setMinimumPointsToPass(event.minimumPointsToPass);
    }
  }, [event]);

  useEffect(() => {
    const timeout = setTimeout(async () => {
      const q = userQuery.trim();
      if (!q) { setUserOptions([]); return; }
      const isNum = /^\d+$/.test(q);
      const tasks: Promise<any>[] = [];
      tasks.push(triggerUserSearch({ name: q, pageNumber: 1, pageSize: 25 }).unwrap());
      tasks.push(triggerUserSearch({ email: q, pageNumber: 1, pageSize: 25 }).unwrap());
      tasks.push(triggerUserSearch({ isuGroup: q, pageNumber: 1, pageSize: 25 }).unwrap());
      if (isNum) tasks.push(triggerUserSearch({ isuNumber: Number(q), pageNumber: 1, pageSize: 25 }).unwrap());
      const settled = await Promise.allSettled(tasks);
      const merged: Record<string, any> = {};
      settled.forEach(r => {
        if (r.status === 'fulfilled') {
          for (const u of (r.value?.users || [])) merged[u.id] = u;
        }
      });
      setUserOptions(Object.values(merged));
    }, 300);
    return () => clearTimeout(timeout);
  }, [userQuery, triggerUserSearch]);

  const handleSave = async () => {
    if (!event) return;
    await updateEvent({
      id: eventId,
      name,
      description,
      type,
      startTime: new Date(startTime).toISOString(),
      endTime: new Date(endTime).toISOString(),
      duration: event.duration || '00:45:00',
      minimumPointsToPass,
      groupId: event.groupId,
    }).unwrap();
    await refetchEvent();
    setEditMode(false);
  };

  const handleAddUser = async (userId: string) => {
    await addToUsers({ id: eventId, userIds: [userId] }).unwrap();
    await refetchUsers();
    setUserQuery('');
    setUserOptions([]);
  };

  const handleAddGroup = async () => {
    if (!groupId) return;
    await grantForGroup({ eventId, groupId }).unwrap();
    await refetchUsers();
    setGroupId('');
  };

  const handleRemoveUser = async (userId: string) => {
    if (!window.confirm('Удалить пользователя из мероприятия?')) return;
    await removeUserFromEvent({ eventId, userId }).unwrap();
    await refetchUsers();
  };

  if (!event) {
    return (
      <Backdrop onClick={onClose}>
        <Modal onClick={e => e.stopPropagation()}>
          <div>Загрузка...</div>
        </Modal>
      </Backdrop>
    );
  }
  const users = usersPage?.users || [];
  const totalUsers = usersPage?.number || 0;

  return (
    <Backdrop onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Row style={{ justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>Мероприятие: {event.name}</h3>
          <Row>
            {!editMode && <Btn onClick={() => setEditMode(true)}>Редактировать</Btn>}
            <Btn onClick={onClose}>Закрыть</Btn>
          </Row>
        </Row>

        {editMode ? (
          <>
            <Grid2>
              <div>
                <Label>Название</Label>
                <Input value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div>
                <Label>Тип</Label>
                <Select value={type} onChange={e => setType(e.target.value as any)}>
                  <option value="Homework">Домашка</option>
                  <option value="Contest">Контест</option>
                  <option value="Milestone">Мильстоун</option>
                  <option value="Exam">Экзамен</option>
                </Select>
              </div>
            </Grid2>
            <div style={{ marginTop: 12 }}>
              <Label>Описание</Label>
              <Textarea value={description} onChange={e => setDescription(e.target.value)} />
            </div>
            <Grid2 style={{ marginTop: 12 }}>
              <div>
                <Label>Начало</Label>
                <Input type="datetime-local" value={startTime} onChange={e => setStartTime(e.target.value)} />
              </div>
              <div>
                <Label>Окончание</Label>
                <Input type="datetime-local" value={endTime} onChange={e => setEndTime(e.target.value)} />
              </div>
            </Grid2>
            <div style={{ marginTop: 12 }}>
              <Label>Минимальный балл для прохождения</Label>
              <Input type="number" value={minimumPointsToPass} onChange={e => setMinimumPointsToPass(Number(e.target.value))} />
            </div>
            <Row style={{ marginTop: 16, justifyContent: 'flex-end' }}>
              <Btn onClick={() => setEditMode(false)}>Отмена</Btn>
              <BtnPrimary onClick={handleSave}>Сохранить</BtnPrimary>
            </Row>
          </>
        ) : (
          <>
            <div style={{ color: '#667', marginBottom: 8 }}>
              <strong>Тип:</strong> {event.type} | <strong>Мин. балл:</strong> {event.minimumPointsToPass}
            </div>
            <div style={{ color: '#667', marginBottom: 8 }}>
              <strong>Начало:</strong> {getDayjsInGMT(event.startTime).format('DD.MM.YYYY HH:mm')} | 
              <strong> Окончание:</strong> {getDayjsInGMT(event.endTime).format('DD.MM.YYYY HH:mm')}
            </div>
            <div style={{ color: '#667' }}>{event.description}</div>
          </>
        )}

        <Section>
          <SectionTitle>Добавить доступ</SectionTitle>
          <Grid2>
            <div>
              <Label>Добавить группу</Label>
              <Row>
                <Select value={groupId} onChange={e => setGroupId(e.target.value)}>
                  <option value="">Выберите группу...</option>
                  {groups?.groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </Select>
                <Btn onClick={handleAddGroup} disabled={!groupId}>Добавить</Btn>
              </Row>
            </div>
            <div>
              <Label>Добавить пользователя</Label>
              <ComboWrap>
                <Input
                  placeholder="Имя/почта/ИСУ..."
                  value={userQuery}
                  onChange={e => { setUserQuery(e.target.value); setUserOpen(true); }}
                  onFocus={() => setUserOpen(true)}
                  onBlur={() => setTimeout(() => setUserOpen(false), 120)}
                />
                {userOpen && userOptions.length > 0 && (
                  <ComboList>
                    {userOptions.map(u => (
                      <ComboItem key={u.id} onMouseDown={() => handleAddUser(u.id)}>
                        {u.name} ({u.isuGroup || '—'})
                      </ComboItem>
                    ))}
                  </ComboList>
                )}
              </ComboWrap>
            </div>
          </Grid2>
        </Section>

        <Section>
          <SectionTitle>Участники ({totalUsers})</SectionTitle>
          <UserList>
            {users.map(u => (
              <UserRow key={u.id}>
                <div>
                  <div style={{ fontWeight: 500 }}>{u.name}</div>
                  <div style={{ fontSize: 12, color: '#667' }}>
                    {u.email} | ИСУ: {u.isuNumber} | Группа: {u.isuGroup || '—'}
                  </div>
                </div>
                <Btn onClick={() => handleRemoveUser(u.id)}>Удалить</Btn>
              </UserRow>
            ))}
          </UserList>
          {totalUsers > pageSize && (
            <Row style={{ justifyContent: 'center', marginTop: 12, gap: 4 }}>
              {Array.from({ length: Math.ceil(totalUsers / pageSize) }, (_, i) => i + 1).map(p => (
                <Btn key={p} disabled={p === pageNumber} onClick={() => setPageNumber(p)}>
                  {p}
                </Btn>
              ))}
            </Row>
          )}
        </Section>
      </Modal>
    </Backdrop>
  );
};
